import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import isEmpty from 'lodash-es/isEmpty';

import { goodOptionValidation } from '@ecp/common/src/utils/good/goodUtil';
import { NumberFormat } from '@ecp/common/src/utils/utils';
import useGroupGoodOption from '@ecp/common/src/hooks/good/option/useGroupGoodOption';
import useGoodOption from '@ecp/common/src/hooks/good/option/useGoodOption';
import useGoodOptionValidator from '@ecp/common/src/hooks/good/option/useGoodOptionValidator';
import theme from '@ecp/common/src/style/theme/default';
import { FlexBoxColumn, FlexBoxSpaceBetween, ImageBox } from '@ecp/common/src/layouts/flex/styled';
import { Spacing } from '@ecp/common/src/layouts/Spacing';
import { TextBox } from '@ecp/common/src/text/TextBox';
import Popup from '@ecp/common/src/components/modal/Popup';
import { DIALOG_MESSAGE, PD_GOOD_CMPS_DIVI, PD_GOOD_TYPE } from '@ecp/common/src/const/good';
// ECMJ 프로그램 아이디 : REQ_대한항공 적립몰_01_01 (jg26.kim)
import { SITE_SKYPASS, SITE_KOREANAIR_SAVINGS } from '@ecp/common/src/const/site/siteNumberConst.js';

import { goodOptionListState } from '@fo-recoil/good/atom';
import { withOptionTotalPrice } from '@fo-recoil/good/selector';
import { withCartBadgeCount } from '@fo-recoil/order/selector';
import { withAdultCertification } from '@fo-recoil/common/user/selector';
import useSiteInfo from '@fo-hooks/common/useSiteInfo';
import { useAlertDialog, useConfirmDialog } from '@fo-hooks/common/useModal';
import goodApi from '@fo-apis/good/goodApi';
import orderApi from '@fo-apis/order/orderApi';
import recopickApi from '@fo-apis/interface/recopickApi';
import InformationLimitPop from '@fo-components/good/InformationLimitPop';
import { CartStyleListCheckWrap } from '@fo-components/order/cart/styled/ShoppingCartCommonStyled';
import CartPopupOptionSelect from '@fo-pages/good/popup/components/CartPopupOptionSelect';
import GoodSelectedOption from '@fo-pages/good/summary/option/GoodSelectedOption';

//정기배송상품은 장바구니 불가라 관련 데이터는 NULL 혹은 빈값으로 처리
const CartPopup = ({ goodNumber, open, setCartPopupOpen, isPriceOpen, handleCartPopupSuccess }) => {
  const optionSelectRef = useRef(null);
  const [goodDetail, setGoodDetail] = useState({});
  const [adultPopupOpen, setAdultPopupOpen] = useState(false);
  const { showAlertDialog } = useAlertDialog();
  const { showConfirmDialog } = useConfirmDialog();
  const navigate = useNavigate();
  const [confirmButtonText, setConfirmButtonText] = useState('장바구니 담기');
  const [confirmButtonType, setConfirmButtonType] = useState('Primary');
  const { priceFormat } = useSiteInfo();

  const [selectedOptionList, setSelectedOptionList] = useRecoilState(goodOptionListState);
  const setCartBadgeCount = useSetRecoilState(withCartBadgeCount);
  const totalPrice = useRecoilValue(withOptionTotalPrice);
  const isAdultVerified = useRecoilValue(withAdultCertification);
  const { siteNumber } = useSiteInfo();
  // ECMJ 프로그램 아이디 : REQ_대한항공 적립몰_01_01 (jg26.kim)
  const isSkypass = useMemo(() => siteNumber === SITE_SKYPASS || siteNumber === SITE_KOREANAIR_SAVINGS, [siteNumber]);

  const isGroup = useMemo(() => {
    return goodDetail?.goodDivisionCode === PD_GOOD_CMPS_DIVI.GROUP;
  }, [goodDetail?.goodDivisionCode]);

  const isRental = useMemo(() => {
    return PD_GOOD_TYPE.RENTAL === goodDetail?.goodTypeCode;
  }, [goodDetail?.goodTypeCode]);

  const isRegularDelivery = useMemo(() => {
    return PD_GOOD_TYPE.REGULAR_DELIVERY === goodDetail?.goodTypeCode;
  }, [goodDetail?.goodTypeCode]);

  const { optionDropdownValidator, optionCtaButtonValidator } = useGoodOptionValidator(showAlertDialog);

  const { subGoodDetail, subGoodItemList, handleGroupDropDownOption } = useGroupGoodOption({
    validator: optionDropdownValidator,
    subGoodList: goodDetail?.subGoodList,
    goodOptionList: selectedOptionList,
    setGoodOptionList: setSelectedOptionList,
  });

  const { selectedGoodDetail, sortedSelectedOptionList, additionItemList, handleDropDownOption } = useGoodOption({
    isGroup,
    goodDetail,
    subGoodDetail,
    goodOptionList: selectedOptionList,
    setGoodOptionList: setSelectedOptionList,
    optionDropdownValidator,
  });

  const imgPath = useMemo(
    () => goodDetail.thumbnailImageUrl03 || goodDetail.thumbnailImageUrl02 || goodDetail.thumbnailImageUrl01,
    [goodDetail]
  );

  const selectedItemList = useMemo(() => {
    return selectedOptionList?.map((opt) => ({
      goodDivisionCode: goodDetail.goodDivisionCode,
      goodTypeCode: goodDetail.goodTypeCode,
      goodNumber: opt?.goodNumber,
      itemNumber: opt?.itemNumber,
      orderQuantity: opt?.count,
      discountPrice: opt?.discountPrice,
      discountFormattedPrice: opt?.discountFormattedPrice,
      orderAmount: opt?.discountPrice * opt?.count,
      additionalItem: opt?.additionalItem,
      virtualVendorNumber: opt?.virtualVendorNumber,
      itemComposeValName01: opt?.itemComposeValName01,
      itemComposeValName02: opt?.itemComposeValName02,
      itemComposeValName03: opt?.itemComposeValName03,
      itemComposeValName04: opt?.itemComposeValName04,
      itemComposeValName05: opt?.itemComposeValName05,
    }));
  }, [goodDetail.goodDivisionCode, goodDetail.goodTypeCode, selectedOptionList]);

  const fetchGoodDetail = useCallback(async () => {
    setGoodDetail({});
    const { result = {} } = await goodApi.getGoodDetail(goodNumber);
    if (!isEmpty(result)) {
      setGoodDetail(result);
      setAdultPopupOpen(result.adultGood);
      setConfirmButtonType(result.stopSale || result.soldOut || !result.showBtnCart ? 'Third' : 'Primary');
      setConfirmButtonText(
        result.stopSale
          ? '판매중지'
          : result.soldOut
          ? '품절'
          : !result.showBtnCart
          ? '장바구니 불가 상품입니다'
          : '장바구니 담기'
      );
    } else {
      showAlertDialog('상품이 존재하지 않습니다.', handleCloseCartPopup);
    }
  }, [goodNumber, setAdultPopupOpen]);

  useEffect(() => {
    (async () => {
      if (goodNumber && open) {
        await fetchGoodDetail();
      }
    })();
  }, [open, goodNumber]);

  const handleLimitConfirm = useCallback(async () => {
    if (open) {
      await fetchGoodDetail();
      setAdultPopupOpen(false);
    }
  }, [goodNumber, open]);

  const handleCloseCartPopup = useCallback(() => {
    setGoodDetail({});
    setCartPopupOpen(false);
    setAdultPopupOpen(false);
    setSelectedOptionList([]);
  }, [setGoodDetail, setCartPopupOpen, setAdultPopupOpen]);

  const orderValidator = useCallback(() => {
    return optionCtaButtonValidator(selectedOptionList, isRegularDelivery, goodDetail?.regularDelivery, null);
  }, [isRegularDelivery, optionCtaButtonValidator, goodDetail?.regularDelivery, selectedOptionList]);

  const handleCartBtnClick = async () => {
    if (orderValidator()) {
      // 장바구니 담기
      if (goodDetail.showBtnBulkp) {
        const currentOrderQuantity = selectedItemList.reduce((acc, elem) => {
          return acc + (elem.additionalItem ? 0 : elem.orderQuantity);
        }, 0);
        if (goodDetail.bulkpOrdPossibleMinQty <= currentOrderQuantity) {
          showAlertDialog(DIALOG_MESSAGE.OPTION_QTY_IS_ONLY_BULKP_ORDER);
          return;
        }
      }

      const { result: availableResult = [] } = await goodApi.checkOrderAvailableWithCart(selectedItemList);
      const { message = '' } = goodOptionValidation({
        checkOrderAvailableResult: availableResult,
        selectedOptionList: selectedItemList,
        isBulkpOrder: false,
        priceFormat,
        ...goodDetail,
      });

      if (message) {
        showAlertDialog(message);
        return false;
      }

      const itemList = selectedItemList?.map((option) => ({
        goodNumber: option?.goodNumber,
        itemNumber: option?.itemNumber,
        virtualVendorNumber: option?.virtualVendorNumber,
        orderQuantity: option?.orderQuantity,
      }));
      const { code, result } = await orderApi.createCart(itemList, '10', null, false, 'POPUP');
      if (code === '200') {
        setCartBadgeCount(result?.cartCount);
        showConfirmDialog(
          DIALOG_MESSAGE.CART_SUCCESS,
          () => {
            navigate('/cart');
            handleCloseCartPopup();
            handleCartPopupSuccess?.();
          },
          () => {
            handleCloseCartPopup();
            handleCartPopupSuccess?.();
          },
          DIALOG_MESSAGE.GO_TO_CART,
          DIALOG_MESSAGE.CONTINUE_SHOPPING
        ); // 계속 쇼핑하기, 장바구니가기
        if (!isSkypass) {
          await recopickApi.createRecommendationBasketLog('basket', { itemList }, history?.prev, history?.current);
        }
      } else {
        let message;
        if (code === 'OD_14102') {
          message = DIALOG_MESSAGE.CART_MAX_COUNT;
        } else if (code === 'OD_24348') {
          message = DIALOG_MESSAGE.ITEM_SOLD_OUT;
        } else if (code === 'OD_14104') {
          message = DIALOG_MESSAGE.CART_PRICE_EXPS_NOT_AVAILABLE;
        } else {
          message = DIALOG_MESSAGE.CART_FAIL;
        }
        showAlertDialog(message, handleCloseCartPopup);
      }
    }
  };

  return adultPopupOpen && !isAdultVerified ? (
    <InformationLimitPop
      key={`${goodNumber}_limit`}
      open={adultPopupOpen}
      onClose={handleCloseCartPopup}
      onConfirm={handleLimitConfirm}
    />
  ) : (
    !isEmpty(goodDetail) && (
      <Popup
        key={goodNumber}
        rootStyleProps={{ width: '550px', minHeight: '450px' }}
        open={open}
        closable={false}
        onCancelClick={handleCloseCartPopup}
        confirmButtonText={confirmButtonText}
        onConfirmClick={confirmButtonType === 'Primary' ? handleCartBtnClick : handleCloseCartPopup}
        confirmButtonProps={{ type: confirmButtonType }}
      >
        <OptionSelectWrapper ref={optionSelectRef}>
          <GoodBaseInfo>
            <ImageBox
              width={'100px'}
              height={'100px'}
              imageSrc={imgPath}
              radius={'8px'}
              border={`1px solid ${theme.color.line.basic}`}
              backgroundPositionY={'center'}
              justify-content={'center'}
              align-items={'center'}
            />
            <Spacing left={3} />
            <GoodName>{goodDetail.goodName}</GoodName>
          </GoodBaseInfo>
          <CartPopupOptionSelect
            isGroup={isGroup}
            subGoodItemList={subGoodItemList}
            additionItemList={additionItemList}
            subGoodDetail={subGoodDetail}
            selectedGoodDetail={selectedGoodDetail}
            handleSelectOptionContainerHeight={() => {}}
            handleDropDownOption={handleDropDownOption}
            handleGroupDropDownOption={handleGroupDropDownOption}
            itemNodeList={goodDetail?.itemNodeList}
            itemList={goodDetail?.itemList}
            stockManagement={goodDetail?.stockManagement}
            ordPossibleMinQty={goodDetail?.ordPossibleMinQty}
            ordPossibleMinAmount={goodDetail?.ordPossibleMinAmount}
            ordPossibleMaxQty={goodDetail?.ordPossibleMaxQty}
            ordPossibleMaxAmount={goodDetail?.ordPossibleMaxAmount}
            ordPossibleMaxQtyStandardCode={goodDetail?.ordPossibleMaxQtyStandardCode}
            familyDiscountTarget={goodDetail?.familyDiscountTarget}
            multiItem={goodDetail?.multiItem}
            itemComposeName01={goodDetail?.itemComposeName01}
            itemComposeName02={goodDetail?.itemComposeName02}
            itemComposeName03={goodDetail?.itemComposeName03}
            itemComposeName04={goodDetail?.itemComposeName04}
            itemComposeName05={goodDetail?.itemComposeName05}
          />

          <OptionBox>
            {sortedSelectedOptionList?.map((option, index) => (
              <GoodSelectedOption
                key={`${option.key}__${index}`}
                index={index}
                floatingType={true}
                showDivider={false}
                option={option}
                isGroup={isGroup}
                isRental={isRental}
                isRegularDelivery={isRegularDelivery}
                stockQtyDisplay={goodDetail?.stockQtyDisplay}
                isPriceOpen={isPriceOpen}
                showBtnBulkp={goodDetail?.showBtnBulkp}
                isCartPopup={true}
                selectedOptionList={selectedOptionList}
                ordPossibleMaxQtyStandardCode={goodDetail?.ordPossibleMaxQtyStandardCode}
                ordPossibleMaxQty={goodDetail?.ordPossibleMaxQty}
                ordPossibleMinQty={goodDetail?.ordPossibleMinQty}
                ordPossibleMaxAmount={goodDetail?.ordPossibleMaxAmount}
                ordPossibleMaxFormattedAmount={goodDetail?.ordPossibleMaxFormattedAmount}
                ordPossibleMinAmount={goodDetail?.ordPossibleMinAmount}
                ordPossibleMinFormattedAmount={goodDetail?.ordPossibleMinFormattedAmount}
                bulkpOrdPossibleMinQty={goodDetail?.bulkpOrdPossibleMinQty}
              />
            ))}
          </OptionBox>

          {isPriceOpen && (
            <CartGoodOptionContainer width={'100%'}>
              <TextBox size={theme.font.size.table} weight={theme.font.weight.medium}>
                총 금액
              </TextBox>
              <TextBox size={theme.font.size.title} weight={theme.font.weight.bold}>
                {NumberFormat(totalPrice)}
                {priceFormat}
              </TextBox>
            </CartGoodOptionContainer>
          )}
        </OptionSelectWrapper>
      </Popup>
    )
  );
};

export default CartPopup;

CartPopup.propTypes = {
  goodNumber: PropTypes.string,
  open: PropTypes.bool,
  setCartPopupOpen: PropTypes.func,
  isPriceOpen: PropTypes.bool,
  handleCartPopupSuccess: PropTypes.func,
};

const OptionSelectWrapper = styled(FlexBoxColumn)`
  width: 100%;
`;

const GoodBaseInfo = styled(CartStyleListCheckWrap)`
  width: 100%;
  margin: 30px 0 15px;
  padding: 0;
`;

const GoodName = styled.p`
  width: 300px;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  font-size: 14px;
  font-weight: 300;
`;

const OptionBox = styled(FlexBoxColumn)`
  margin-top: 20px;
  width: 100%;
`;

const CartGoodOptionContainer = styled(FlexBoxSpaceBetween)`
  width: 100%;
  background-color: ${theme.color.background.basic};
  padding: 15px;
  margin-top: 5px;
`;
